/*
  Product lightbox — replaces the Dawn product-media modal.

  <product-lightbox> opens from the gallery's <modal-opener> (same id as before:
  ProductModal-{section.id}), shows one media item at a time fitted inside the
  viewport, lets the visitor swipe / arrow / keyboard between media, switch with
  the thumbnail strip, and zoom images (double-tap or pinch on touch, click on
  mouse) while panning. Closing hands the media that was being viewed back to
  the main gallery so the page stays in sync.

  Extends ModalDialog (global.js) for the open/close, focus-trap and body
  scroll-lock behaviour the rest of the theme uses.
*/
(function () {
  if (typeof ModalDialog === 'undefined' || customElements.get('product-lightbox')) return;

  const MIN_SCALE = 1;
  const MAX_SCALE = 4;
  const TAP_ZOOM = 2.5;
  const DOUBLE_TAP_MS = 320;
  const DOUBLE_TAP_PX = 30;
  const CLICK_SLOP_PX = 6;

  class Zoomable {
    constructor(container, img) {
      this.container = container;
      this.img = img;
      this.scale = 1;
      this.tx = 0;
      this.ty = 0;
      this.pointers = new Map();
      this.lastTap = 0;
      this.lastTapPoint = null;
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      container.addEventListener('pointerdown', this.onPointerDown);
      container.addEventListener('pointermove', this.onPointerMove);
      container.addEventListener('pointerup', this.onPointerUp);
      container.addEventListener('pointercancel', this.onPointerUp);
      container.addEventListener('lostpointercapture', this.onPointerUp);
      container.addEventListener('dragstart', (e) => e.preventDefault());
    }

    get zoomed() {
      return this.scale > 1.001;
    }

    /* The image's fitted (scale 1) box inside the container, in container coordinates. */
    baseRect() {
      const c = this.container.getBoundingClientRect();
      const natural = this.img.naturalWidth && this.img.naturalHeight
        ? this.img.naturalWidth / this.img.naturalHeight
        : (parseFloat(this.img.getAttribute('width')) || 1) / (parseFloat(this.img.getAttribute('height')) || 1);
      let w = c.width;
      let h = w / natural;
      if (h > c.height) {
        h = c.height;
        w = h * natural;
      }
      return { x: (c.width - w) / 2, y: (c.height - h) / 2, w, h, cw: c.width, ch: c.height };
    }

    clamp() {
      const b = this.baseRect();
      const sw = b.w * this.scale;
      const sh = b.h * this.scale;
      // tx/ty move the image from its fitted position; keep the viewport covered.
      if (sw <= b.cw) this.tx = (b.cw - sw) / 2 - b.x;
      else this.tx = Math.min(-b.x, Math.max(b.cw - b.x - sw, this.tx));
      if (sh <= b.ch) this.ty = (b.ch - sh) / 2 - b.y;
      else this.ty = Math.min(-b.y, Math.max(b.ch - b.y - sh, this.ty));
    }

    apply(animate) {
      this.img.classList.toggle('is-animating', !!animate);
      this.img.style.transform = this.zoomed
        ? `translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`
        : '';
      this.container.classList.toggle('is-zoomed', this.zoomed);
      this.container.dispatchEvent(new CustomEvent('lightbox:zoom', { bubbles: true, detail: { zoomed: this.zoomed } }));
    }

    reset(animate) {
      this.scale = 1;
      this.tx = 0;
      this.ty = 0;
      this.apply(animate);
    }

    /* Zoom to `scale` keeping the container point (px, py) fixed on screen. */
    zoomAt(scale, px, py, animate) {
      const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
      const ratio = next / this.scale;
      const b = this.baseRect();
      // image point under (px,py): (px - b.x - tx) / scale  → keep it under (px,py) at the new scale
      this.tx = px - b.x - (px - b.x - this.tx) * ratio;
      this.ty = py - b.y - (py - b.y - this.ty) * ratio;
      this.scale = next;
      this.clamp();
      this.apply(animate);
    }

    toggleAt(px, py) {
      if (this.zoomed) this.reset(true);
      else this.zoomAt(TAP_ZOOM, px, py, true);
    }

    localPoint(e) {
      const c = this.container.getBoundingClientRect();
      return { x: e.clientX - c.left, y: e.clientY - c.top };
    }

    onPointerDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      try { this.container.setPointerCapture(e.pointerId); } catch (err) { /* synthetic events have no active pointer */ }
      this.pointers.set(e.pointerId, this.localPoint(e));
      this.moved = false;
      if (this.pointers.size === 2) {
        const [a, b] = [...this.pointers.values()];
        this.pinch = {
          dist: Math.hypot(a.x - b.x, a.y - b.y),
          mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
          scale: this.scale,
          tx: this.tx,
          ty: this.ty,
        };
        this.pan = null;
      } else if (this.pointers.size === 1) {
        const p = this.pointers.get(e.pointerId);
        this.pan = { x: p.x, y: p.y, tx: this.tx, ty: this.ty };
        this.downPoint = p;
        this.downTime = Date.now();
      }
    }

    onPointerMove(e) {
      if (!this.pointers.has(e.pointerId)) return;
      const p = this.localPoint(e);
      this.pointers.set(e.pointerId, p);
      if (this.pointers.size >= 2 && this.pinch) {
        const [a, b] = [...this.pointers.values()];
        const dist = Math.hypot(a.x - b.x, a.y - b.y);
        const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        const bb = this.baseRect();
        const next = Math.min(MAX_SCALE, Math.max(0.5, this.pinch.scale * (dist / this.pinch.dist)));
        const ratio = next / this.pinch.scale;
        // keep the image point that was under the start midpoint under the current midpoint
        this.tx = mid.x - bb.x - (this.pinch.mid.x - bb.x - this.pinch.tx) * ratio;
        this.ty = mid.y - bb.y - (this.pinch.mid.y - bb.y - this.pinch.ty) * ratio;
        this.scale = next;
        if (this.scale >= 1) this.clamp();
        this.moved = true;
        this.apply(false);
        e.preventDefault();
      } else if (this.pointers.size === 1 && this.pan && this.zoomed) {
        this.tx = this.pan.tx + (p.x - this.pan.x);
        this.ty = this.pan.ty + (p.y - this.pan.y);
        if (Math.hypot(p.x - this.pan.x, p.y - this.pan.y) > CLICK_SLOP_PX) this.moved = true;
        this.clamp();
        this.apply(false);
        e.preventDefault();
      }
    }

    onPointerUp(e) {
      if (!this.pointers.has(e.pointerId)) return;
      const p = this.pointers.get(e.pointerId);
      this.pointers.delete(e.pointerId);
      if (this.pinch) {
        if (this.pointers.size < 2) {
          this.pinch = null;
          if (this.scale < 1.05) this.reset(true);
          else {
            this.clamp();
            this.apply(true);
          }
          // one finger left: continue as a pan from where it is
          const rest = [...this.pointers.values()][0];
          if (rest) this.pan = { x: rest.x, y: rest.y, tx: this.tx, ty: this.ty };
        }
        return;
      }
      if (this.pointers.size === 0 && !this.moved && e.type === 'pointerup') {
        const now = Date.now();
        const quick = now - this.downTime < 500;
        if (!quick) return;
        if (e.pointerType === 'mouse') {
          this.toggleAt(p.x, p.y);
          return;
        }
        const isDouble = this.lastTapPoint && now - this.lastTap < DOUBLE_TAP_MS
          && Math.hypot(p.x - this.lastTapPoint.x, p.y - this.lastTapPoint.y) < DOUBLE_TAP_PX;
        if (isDouble) {
          this.lastTap = 0;
          this.lastTapPoint = null;
          this.toggleAt(p.x, p.y);
        } else {
          this.lastTap = now;
          this.lastTapPoint = p;
        }
      }
      this.pan = null;
    }
  }

  class ProductLightbox extends ModalDialog {
    constructor() {
      super();
      this.track = this.querySelector('[data-lightbox-track]');
      this.slides = Array.from(this.querySelectorAll('.product-lightbox__slide'));
      this.thumbs = Array.from(this.querySelectorAll('[data-lightbox-thumb]'));
      this.current = this.querySelector('[data-lightbox-current]');
      this.prevButton = this.querySelector('[data-lightbox-prev]');
      this.nextButton = this.querySelector('[data-lightbox-next]');
      this.index = 0;
      this.zoomables = [];
      this.querySelectorAll('[data-lightbox-zoom]').forEach((container) => {
        const img = container.querySelector('img');
        if (img) this.zoomables.push(new Zoomable(container, img));
      });

      this.onScroll = this.onScroll.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.goTo(i, true)));
      if (this.prevButton) this.prevButton.addEventListener('click', () => this.goTo(this.index - 1, true));
      if (this.nextButton) this.nextButton.addEventListener('click', () => this.goTo(this.index + 1, true));
      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.goTo(this.index - 1, true);
        if (e.key === 'ArrowRight') this.goTo(this.index + 1, true);
      });
      // While an image is zoomed the track must not swipe: the pan owns the gesture.
      this.addEventListener('lightbox:zoom', (e) => {
        this.classList.toggle('is-zoomed', e.detail.zoomed);
      });
    }

    indexOfMedia(mediaId) {
      const i = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      return i < 0 ? 0 : i;
    }

    show(opener) {
      super.show(opener);
      const mediaId = opener && opener.getAttribute('data-media-id');
      this.openedMediaId = mediaId;
      // The track only has a size once [open] is set; jump after layout.
      requestAnimationFrame(() => {
        this.goTo(this.indexOfMedia(mediaId), false);
        this.setActive(this.index);
      });
    }

    hide() {
      const slide = this.slides[this.index];
      super.hide();
      this.zoomables.forEach((z) => z.reset(false));
      // Hand the last viewed media back to the gallery so the page matches.
      if (slide && slide.dataset.mediaId !== this.openedMediaId) {
        const gallery = document.getElementById(`MediaGallery-${this.dataset.sectionId}`);
        if (gallery && typeof gallery.setActiveMedia === 'function') {
          gallery.setActiveMedia(`${this.dataset.sectionId}-${slide.dataset.mediaId}`, false);
        }
      }
    }

    goTo(index, smooth) {
      const i = Math.max(0, Math.min(this.slides.length - 1, index));
      this.zoomables.forEach((z) => z.reset(false));
      this.track.scrollTo({ left: i * this.track.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
      this.setActive(i);
    }

    onScroll() {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => {
        const i = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
        if (i !== this.index) this.setActive(i);
      }, 80);
    }

    setActive(i) {
      const previous = this.index;
      this.index = i;
      if (previous !== i) {
        this.zoomables.forEach((z) => z.reset(false));
        if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
      }
      if (this.current) this.current.textContent = i + 1;
      this.thumbs.forEach((thumb, t) => {
        if (t === i) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      const active = this.thumbs[i];
      const strip = active && active.closest('[data-lightbox-thumbs]');
      if (active && strip) {
        // centre the active thumbnail in the strip (scrollIntoView could move the page)
        const target = active.offsetLeft - (strip.clientWidth - active.offsetWidth) / 2;
        strip.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
      }
      if (this.prevButton) this.prevButton.toggleAttribute('disabled', i === 0);
      if (this.nextButton) this.nextButton.toggleAttribute('disabled', i === this.slides.length - 1);
    }
  }

  customElements.define('product-lightbox', ProductLightbox);
})();
